import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ABSpinner, useErrorService } from '@abroad/components'
import API from '../../utils/API'
import { getProgramDatesRange } from '../../utils/date'
import {
  formatExperienceDateRange,
  getListStatusMeta,
  getTemporalStatus,
} from './experienceStatus'

const PAGE_LIMIT = 25

const asRowArray = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.quests) ? data.quests : []

const sortByStartDate = (rows) =>
  [...rows].sort(
    (a, b) =>
      +new Date(getProgramDatesRange(b).start) -
      +new Date(getProgramDatesRange(a).start),
  )

/**
 * forCopyFrom list omits isPublished/published, so getListStatusMeta would
 * treat every row as Draft. Fall back to programme-date status in that case.
 */
export const getCopyQuestDisplay = (quest) => {
  const title = quest?.title || quest?.name || `Experience ${quest?._id || ''}`
  const dateRange = formatExperienceDateRange(quest)
  const hasPublishFlag = quest?.isPublished != null || quest?.published != null

  let status
  if (hasPublishFlag) {
    status = getListStatusMeta(quest)
  } else {
    const temporal = getTemporalStatus(quest)
    if (temporal.key === 'run') {
      status = { label: 'In progress', chipClass: 'badge-run' }
    } else if (temporal.key === 'done') {
      status = { label: 'Completed', chipClass: 'badge-done' }
    } else {
      status = { label: 'Upcoming', chipClass: 'badge-soon' }
    }
  }

  return {
    title,
    subtitle: dateRange ? `${status.label} · ${dateRange}` : status.label,
    chipLabel: status.label,
    chipClass: status.chipClass,
  }
}

export const SearchIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='13'
    height='13'
    viewBox='0 0 13 13'
    fill='none'
    aria-hidden='true'>
    <line
      x1='8.06'
      y1='8'
      x2='12'
      y2='11.94'
      stroke='#93959B'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
    <circle cx='5' cy='5' r='4.25' stroke='#93959B' strokeWidth='1.5' />
  </svg>
)

const ExperienceCopyModal = ({
  show,
  onHide,
  onSelect,
  currentQuestId,
  title,
  lead,
  isLoadingSelection = false,
}) => {
  const Error = useErrorService()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [quests, setQuests] = useState([])
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const fetchInFlightRef = useRef(false)
  const hasMoreRef = useRef(false)

  useEffect(() => {
    if (!show) {
      setSearch('')
      setDebouncedSearch('')
      return undefined
    }
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      search ? 350 : 0,
    )
    return () => clearTimeout(timer)
  }, [show, search])

  const fetchQuests = useCallback(
    async ({ query = '', pageNum = 1, append = false } = {}) => {
      if (fetchInFlightRef.current) return
      fetchInFlightRef.current = true

      try {
        if (append) {
          setIsLoadingMore(true)
        } else {
          setIsLoading(true)
        }

        const { data } = await API.AdminAPI.quests.getQuestList({
          page: pageNum,
          limit: PAGE_LIMIT,
          name: query || undefined,
          forCopyFrom: true,
        })

        const rawList = asRowArray(data)
        const more = rawList.length === PAGE_LIMIT
        hasMoreRef.current = more
        setPage(pageNum)

        const rows = sortByStartDate(rawList).filter(
          (quest) => quest?._id !== currentQuestId,
        )

        if (append) {
          setQuests((prev) => {
            const ids = new Set(prev.map((q) => q._id || q.id))
            return sortByStartDate([
              ...prev,
              ...rows.filter((q) => !ids.has(q._id || q.id)),
            ])
          })
        } else {
          setQuests(rows)
        }
      } catch (e) {
        Error.showError(e)
        if (!append) {
          setQuests([])
          hasMoreRef.current = false
        }
      } finally {
        fetchInFlightRef.current = false
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    // Error from useErrorService() is a new object every render — do not depend on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentQuestId],
  )

  useEffect(() => {
    if (!show) {
      setQuests([])
      setPage(1)
      hasMoreRef.current = false
      return undefined
    }

    fetchQuests({ query: debouncedSearch, pageNum: 1, append: false })
    return undefined
  }, [show, debouncedSearch, fetchQuests])

  const handleListScroll = (e) => {
    const el = e.currentTarget
    if (
      !hasMoreRef.current ||
      fetchInFlightRef.current ||
      isLoading ||
      isLoadingMore
    ) {
      return
    }
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      fetchQuests({
        query: debouncedSearch,
        pageNum: page + 1,
        append: true,
      })
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoadingSelection) {
      onHide()
    }
  }

  const handleSelect = (quest) => {
    if (isLoadingSelection) {
      return
    }
    onSelect({
      value: quest._id,
      label: quest.title || quest.name || `Quest ${quest._id}`,
      extra: quest,
    })
  }

  if (!show) {
    return null
  }

  return (
    <div
      className='experience-copy-modal-backdrop open'
      role='dialog'
      aria-modal='true'
      aria-labelledby='experienceCopyModalTitle'
      onClick={handleBackdropClick}>
      <div className='experience-copy-modal-card'>
        <h2 id='experienceCopyModalTitle'>{title}</h2>
        {lead ? <p className='experience-copy-modal-lead'>{lead}</p> : null}

        <label className='experience-copy-search'>
          <input
            type='search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search experiences…'
            autoComplete='off'
            aria-label='Search experiences'
          />
          <SearchIcon />
        </label>

        <div className='experience-copy-list' onScroll={handleListScroll}>
          {isLoading ? (
            <div className='text-center py-4'>
              <ABSpinner size='sm' />
            </div>
          ) : (
            <>
              {quests.map((quest) => {
                const display = getCopyQuestDisplay(quest)
                return (
                  <button
                    key={quest._id}
                    type='button'
                    className='experience-copy-source'
                    disabled={isLoadingSelection}
                    onClick={() => handleSelect(quest)}>
                    <div>
                      <b className='fw-bold'>{display.title}</b>
                      <span>{display.subtitle}</span>
                    </div>
                    <span
                      className={`badge-experience experience-copy-chip ${display.chipClass}`}>
                      {display.chipLabel}
                    </span>
                  </button>
                )
              })}
              {isLoadingMore ? (
                <div className='text-center py-3'>
                  <ABSpinner size='sm' />
                </div>
              ) : null}
            </>
          )}
        </div>

        {!isLoading && quests.length === 0 ? (
          <p className='experience-copy-empty show'>
            No experiences match that search.
          </p>
        ) : null}

        <div className='experience-copy-modal-actions'>
          <button
            type='button'
            className='btn-outline-experience'
            onClick={onHide}
            disabled={isLoadingSelection}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

ExperienceCopyModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  currentQuestId: PropTypes.string,
  title: PropTypes.string.isRequired,
  lead: PropTypes.string,
  isLoadingSelection: PropTypes.bool,
}

export default ExperienceCopyModal
